import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

// Configuration matching OrderLine exactly
const OFFSET_CONFIG = {
  BUTTON_WIDTH: 24,
  BUTTON_HEIGHT: 18,
  BUTTON_AREA_WIDTH: 115,
  STOPS_BUTTON_OFFSET: 18,
  STOPS_BUTTON_HEIGHT: 14,
  VERTICAL_PADDING: 5,
  SPACING_BETWEEN_LEVELS: 8,
  OFFSET_FROM_EDGE: 58,
};

/**
 * HiddenOrdersIndicator - Shows a marker on the left edge when orders are clipped
 *
 * Renders a simple "◀ +N" indicator when orders are pushed beyond the visible area
 * due to multiple orders at the same price level.
 */
class HiddenOrdersIndicator extends Component {
  constructor(props) {
    super(props);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  /**
   * Get the width of an order based on its state (matching OrderLine logic)
   */
  getOrderWidth(orderState) {
    if (orderState === "open") {
      return OFFSET_CONFIG.BUTTON_WIDTH + 80; // trash button + quantity area
    }
    return OFFSET_CONFIG.BUTTON_AREA_WIDTH; // full width for local/pending orders
  }

  /**
   * Calculate which orders would be hidden (X < 0) based on their offset
   * Uses the same logic as OrderLine.calculateDynamicOffsetWithCumulativeSpacing
   */
  calculateHiddenOrders(orders, moreProps) {
    if (!orders || orders.length === 0 || !moreProps?.chartConfig) {
      return { buy: [], sell: [] };
    }

    const {
      chartConfig: { width, yScale, height },
    } = moreProps;
    const hiddenOrders = { buy: [], sell: [] };

    // Calculate footprint for grouping (same as OrderLine)
    const orderFootprint =
      OFFSET_CONFIG.STOPS_BUTTON_OFFSET +
      OFFSET_CONFIG.STOPS_BUTTON_HEIGHT +
      OFFSET_CONFIG.VERTICAL_PADDING;

    // For each order, calculate if it would be hidden
    orders.forEach((order) => {
      if (!order.price) return;
      const currentY = Math.round(yScale(order.price));
      if (currentY < 0 || currentY > height) return; // Skip orders outside vertical bounds

      // Find overlapping orders (same logic as OrderLine)
      const overlappingOrders = orders.filter((o) => {
        if (!o.price) return false;
        const orderY = Math.round(yScale(o.price));
        const verticalDistance = Math.abs(currentY - orderY);
        return verticalDistance <= orderFootprint;
      });

      // Sort by priority (open orders first) then by Y position
      const sortedOrders = [...overlappingOrders].sort((a, b) => {
        const yA = Math.round(yScale(a.price));
        const yB = Math.round(yScale(b.price));
        const priorityA = a.state === "open" ? 0 : 1;
        const priorityB = b.state === "open" ? 0 : 1;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return yA - yB;
      });

      // Calculate cumulative offset for this order
      const orderOffsets = new Map();
      const offsetToOrder = new Map();
      const cumulativeOffsets = new Map();

      for (let i = 0; i < sortedOrders.length; i++) {
        const o = sortedOrders[i];
        let offset = 0;
        while (offsetToOrder.has(offset) && offset < 10) {
          offset++;
        }
        orderOffsets.set(o.id, offset);
        offsetToOrder.set(offset, o);

        // Calculate cumulative pixel offset
        let cumulativeOffset = 0;
        for (let level = 0; level < offset; level++) {
          const orderAtLevel = offsetToOrder.get(level);
          if (orderAtLevel) {
            const levelWidth = this.getOrderWidth(orderAtLevel.state);
            cumulativeOffset +=
              levelWidth + OFFSET_CONFIG.SPACING_BETWEEN_LEVELS;
          }
        }
        cumulativeOffsets.set(o.id, cumulativeOffset);
      }

      const offsetPixels = cumulativeOffsets.get(order.id) || 0;
      const orderWidth = this.getOrderWidth(order.state);
      const buttonX =
        width - orderWidth - OFFSET_CONFIG.OFFSET_FROM_EDGE - offsetPixels;

      // If button would be off-screen, it's hidden
      if (buttonX < 5) {
        const side = (order.side || "").toLowerCase();
        if (side === "buy" || side === "compra") {
          hiddenOrders.buy.push({ ...order, y: currentY });
        } else {
          hiddenOrders.sell.push({ ...order, y: currentY });
        }
      }
    });

    return hiddenOrders;
  }

  drawOnCanvas(ctx, moreProps) {
    const { orders } = this.props;
    const hiddenOrders = this.calculateHiddenOrders(orders, moreProps);

    const totalHidden = hiddenOrders.buy.length + hiddenOrders.sell.length;
    if (totalHidden === 0) return;

    // Group hidden orders by Y position for indicators
    const indicatorsByY = new Map();

    [...hiddenOrders.buy, ...hiddenOrders.sell].forEach((order) => {
      const yKey = Math.round(order.y / 30) * 30; // Group within 30px
      if (!indicatorsByY.has(yKey)) {
        indicatorsByY.set(yKey, {
          y: order.y,
          count: 0,
          hasBuy: false,
          hasSell: false,
        });
      }
      const indicator = indicatorsByY.get(yKey);
      indicator.count++;
      const side = (order.side || "").toLowerCase();
      if (side === "buy" || side === "compra") {
        indicator.hasBuy = true;
      } else {
        indicator.hasSell = true;
      }
    });

    // Draw indicators
    indicatorsByY.forEach((indicator) => {
      this.drawIndicator(ctx, indicator);
    });
  }

  drawIndicator(ctx, indicator) {
    const { y, count, hasBuy, hasSell } = indicator;
    const x = 8; // Left edge with small padding

    // Determine color based on order types
    let bgColor;
    if (hasBuy && hasSell) {
      bgColor = "#6B7280"; // Gray for mixed
    } else if (hasBuy) {
      bgColor = "#22C55E"; // Green for buy
    } else {
      bgColor = "#EF4444"; // Red for sell
    }

    // Draw pill background
    const text = `+${count}`;
    ctx.font = "bold 10px Arial";
    const textWidth = ctx.measureText(text).width;
    const pillWidth = textWidth + 18; // Arrow + padding
    const pillHeight = 16;
    const pillY = y - pillHeight / 2;

    // Rounded rectangle background
    const radius = 3;
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.moveTo(x + radius, pillY);
    ctx.lineTo(x + pillWidth - radius, pillY);
    ctx.quadraticCurveTo(x + pillWidth, pillY, x + pillWidth, pillY + radius);
    ctx.lineTo(x + pillWidth, pillY + pillHeight - radius);
    ctx.quadraticCurveTo(
      x + pillWidth,
      pillY + pillHeight,
      x + pillWidth - radius,
      pillY + pillHeight,
    );
    ctx.lineTo(x + radius, pillY + pillHeight);
    ctx.quadraticCurveTo(x, pillY + pillHeight, x, pillY + pillHeight - radius);
    ctx.lineTo(x, pillY + radius);
    ctx.quadraticCurveTo(x, pillY, x + radius, pillY);
    ctx.closePath();
    ctx.fill();

    // Draw arrow
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "10px Arial";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("◀", x + 3, y);

    // Draw count
    ctx.font = "bold 10px Arial";
    ctx.fillText(text, x + 12, y);
  }

  render() {
    return (
      <GenericChartComponent
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={["mousemove", "pan", "drag", "zoom", "xAxisZoom", "resize"]}
      />
    );
  }
}

HiddenOrdersIndicator.propTypes = {
  orders: PropTypes.array.isRequired,
};

export default HiddenOrdersIndicator;
